import { useState, useRef } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import AuthShell from "../components/AuthShell";
import whitelogo from "../assets/whiteintel-white-logo-new.png";

const REGISTER_URL = "https://whiteintel.io/api/regular/app/signup_handler.php";

export default function Register() {
  const [name, setName] = useState("");
  const [surName, setSurName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  const handleRegister = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess(false);

    if (password !== confirmPw) {
      setErr("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(REGISTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          surname: surName.trim(),
          company: company.trim(),
          email: email.trim(),
          password,
          reTypePassword: confirmPw,
          "cf-turnstile-response": turnstileToken,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Registration failed");

      setSuccess(true);
    } catch (e) {
      setErr(e.message || "Registration failed");
    } finally {
      setLoading(false);
      if (turnstileRef.current) turnstileRef.current.reset();
      setTurnstileToken("");
    }
  };

  return (
    <AuthShell>
      <div className="w-full max-w-[420px] rounded-2xl border border-indigo-500/15 bg-gradient-to-b from-slate-900/85 to-[#080d1c]/95 backdrop-blur-xl shadow-[0_0_70px_-10px_rgba(99,102,241,0.22),0_28px_70px_-26px_rgba(0,0,0,0.9)]">
        <div className="px-7 pt-7 pb-6">
          <div className="flex items-center gap-3">
            <img src={whitelogo} alt="WhiteIntel" className="h-8 w-auto shrink-0" />
            <h1 className="text-[19px] font-semibold text-white leading-snug">
              Create your account
            </h1>
          </div>
          <p className="mt-3 text-[13px] text-slate-500 leading-relaxed">
            Start with a free account. No credit card required.
          </p>

          {err && (
            <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-[13px] text-red-300">
              <div className="flex items-start justify-between gap-3">
                <span className="pt-0.5">{err}</span>
                <button
                  type="button"
                  onClick={() => setErr("")}
                  className="shrink-0 p-1 rounded hover:bg-red-500/20 text-red-300"
                  aria-label="Dismiss error"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          {success && (
            <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[13px] text-emerald-300">
              Your account has been created. Please check your email (including your Spam/Junk folder) to complete the registration.{" "}
              <a href="/login" className="underline text-indigo-300 hover:text-indigo-200">
                Sign in
              </a>
            </div>
          )}

          {!success && (
            <form onSubmit={handleRegister} className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane"
                    className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400">Surname</label>
                  <input
                    type="text"
                    value={surName}
                    onChange={(e) => setSurName(e.target.value)}
                    placeholder="Doe"
                    className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400">Company</label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Acme Inc."
                  className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400">Password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] pl-3 pr-9 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-300"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400">Confirm password</label>
                <input
                  type={showPw ? "text" : "password"}
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                  autoComplete="new-password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-indigo-900/40 disabled:opacity-50 transition-all"
              >
                {loading ? "Creating account…" : "Sign up"}
              </button>

              <div className="pt-1">
                <Turnstile
                  siteKey="0x4AAAAAABHcWCVoBFGccBYv"
                  onSuccess={(token) => setTurnstileToken(token)}
                  options={{ theme: "dark" }}
                  ref={turnstileRef}
                />
              </div>
            </form>
          )}
        </div>

        <div className="rounded-b-2xl border-t border-indigo-500/15 bg-[#05091a]/60 px-7 py-3 text-[11.5px] text-slate-500">
          Already have an account?{" "}
          <a href="/login" className="text-indigo-300 hover:text-indigo-200">
            Sign in
          </a>
        </div>
      </div>
    </AuthShell>
  );
}
